import { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/features/appData";
import {
  apiGetPolicyProjects,
  apiUpdatePolicyProject,
  apiEmbedPolicyProjectAsResource,
  apiGetUserLinkRecords,
  apiDeletePolicyProject,
  apiUpdatePolicyProjectContent,
} from "../services/PolicyStudioService";
import {
  LinkRecord,
  PolicyAdjustment,
  PolicyProject,
  PolicyProjectType,
} from "winslow-core";
import { postPolicyStudioFileUpload } from "../services/client";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../config";
import { showdownConverter } from "winslow-core";
import html2pdf from "html2pdf.js";
import {
  saveSelectedPolicyId,
  getSelectedPolicyId,
} from "../utils/localStorage";
import { useNavigate } from "react-router-dom";
import useWebSocket from "react-use-websocket";
import config from "environment";
import debounce from "lodash/debounce";

export async function copyMarkdownToClipboard(markdown: string) {
  const html = showdownConverter.makeHtml(markdown);
  if (navigator.clipboard && navigator.clipboard.write) {
    try {
      const blobInput = new Blob([html], { type: "text/html" });
      const clipboardItem = new ClipboardItem({
        "text/html": blobInput,
        "text/plain": new Blob([markdown], { type: "text/plain" }),
      });

      await navigator.clipboard.write([clipboardItem]);
      toast("Copied to clipboard", toastSuccess);
    } catch (err) {
      toast("Unable to copy", toastFail);
    }
  } else {
    toast("Unable to copy", toastFail);
  }
}

export const usePolicyStudio = () => {
  const navigate = useNavigate();
  const storeUser = useSelector(selectUser);
  const previewRef = useRef<HTMLDivElement>(null);
  const [policyProjects, setPolicyProjects] = useState<PolicyProject[]>([]);
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyProject | null>(
    null
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [drawerIsLoading, setDrawerIsLoading] = useState(false);
  const [isConflictsDrawerOpen, setIsConflictsDrawerOpen] = useState(false);
  const [isComplianceDrawerOpen, setIsComplianceDrawerOpen] = useState(false);
  const [linkRecords, setLinkRecords] = useState<LinkRecord[]>([]);

  const [isCreateFromFileModalOpen, setIsCreateFromFileModalOpen] =
    useState(false);

  const { sendJsonMessage, lastJsonMessage } = useWebSocket(
    config.wsPolicyStudioUrl,
    {
      onOpen: () => console.log("[websocket] opened"),
      onClose: (code: any) => console.log("[websocket] closed: ", code),
      shouldReconnect: (closeEvent) => true,
      onError: (event: WebSocketEventMap["error"]) =>
        console.log("[websocket] error: ", event),
    }
  );

  const setStreamingFalse = useMemo(
    () => debounce(() => setIsStreaming(false), 2000),
    [setIsStreaming]
  );

  useEffect(() => {
    return () => {
      setStreamingFalse.cancel();
    };
  }, [setStreamingFalse]);

  useEffect(() => {
    console.log("[PolicyStudio] socket message incoming: ", lastJsonMessage);

    if (lastJsonMessage !== null) {
      const response = lastJsonMessage as {
        policyProject?: PolicyProject;
        content?: string;
        conflicts?: {
          conflictsContent: string;
          linkRecordIds: string[];
        };
        compliance?: {
          complianceContent: string;
          jurisdictionContent: string;
        };
      };

      if (
        response.content ||
        response.conflicts?.conflictsContent ||
        response.compliance?.complianceContent
      ) {
        setIsStreaming(true);
        setStreamingFalse();
      }

      if (response.policyProject) {
        // Handle full policy project updates (initial and final)
        setPolicyProjects((prev) => {
          const exists = prev.some((p) => p.id === response.policyProject!.id);
          if (!exists) {
            return [response.policyProject!, ...prev];
          }
          return prev.map((p) =>
            p.id === response.policyProject!.id ? response.policyProject! : p
          );
        });
        setSelectedPolicy(response.policyProject);
        setIsLoading(false);
      } else if (response.content && selectedPolicy) {
        // Handle content-only updates during streaming
        setSelectedPolicy((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            content: prev.content + response.content!,
          };
        });
      } else if (
        response.conflicts &&
        response.conflicts.conflictsContent &&
        selectedPolicy
      ) {
        setDrawerIsLoading(false);
        setSelectedPolicy((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            conflicts: {
              conflictsContent:
                prev.conflicts?.conflictsContent +
                response.conflicts!.conflictsContent,
              linkRecordIds: prev.conflicts?.linkRecordIds || [],
            },
          };
        });
      } else if (
        response.compliance &&
        response.compliance.complianceContent &&
        selectedPolicy
      ) {
        setDrawerIsLoading(false);
        setSelectedPolicy((prev) => {
          if (!prev) return null;
          return {
            ...prev,
            compliance: {
              complianceContent:
                prev.compliance?.complianceContent +
                response.compliance!.complianceContent,
              jurisdictionContent: prev.compliance?.jurisdictionContent || "",
            },
          };
        });
      } else {
        toast("Unable to update policy project", toastFail);
      }
      setIsLoading(false);
    }
  }, [lastJsonMessage]);

  const loadLinkRecords = async () => {
    if (!storeUser?.id || !storeUser?.currentWorkspaceId) return;

    const records = await apiGetUserLinkRecords(
      storeUser.currentWorkspaceId,
      storeUser.id
    );
    if (records) {
      records.sort((a: LinkRecord, b: LinkRecord) => {
        const aTitle = a.links?.[0]?.title ?? "";
        const bTitle = b.links?.[0]?.title ?? "";
        return aTitle.toLowerCase().localeCompare(bTitle.toLowerCase());
      });
    }
    setLinkRecords(records || []);
    return records;
  };

  useEffect(() => {
    if (!storeUser) {
      navigate("/home");
      return;
    }

    const loadPolicyProjects = async () => {
      try {
        setIsLoading(true);
        const projects = await apiGetPolicyProjects(storeUser.id);
        setPolicyProjects(projects || []);

        const savedPolicyId = getSelectedPolicyId();

        const savedPolicy = projects?.find((p) => p.id === savedPolicyId);

        if (savedPolicy) {
          setSelectedPolicy(savedPolicy);
        } else {
          const newestPolicy = projects?.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )[0];
          setSelectedPolicy(newestPolicy || null);
        }
      } catch (error) {
        console.error("Failed to load policy projects:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadPolicyProjects();
    loadLinkRecords();
  }, [storeUser]);

  useEffect(() => {
    if (selectedPolicy) {
      saveSelectedPolicyId(selectedPolicy?.id || null);
    }
  }, [selectedPolicy]);

  const updatePolicyState = (updatedPolicy: PolicyProject) => {
    const updatedPolicyProjects = policyProjects.map((policy) =>
      policy.id === updatedPolicy.id ? updatedPolicy : policy
    );
    setPolicyProjects(updatedPolicyProjects);
    setSelectedPolicy(updatedPolicy);
  };

  const removeContentFromPolicy = (policy: PolicyProject) => {
    const updatedPolicy = { ...policy, content: "" };
    updatePolicyState(updatedPolicy);
  };

  const policyActions = {
    createPolicy: async (type: PolicyProjectType) => {
      if (!storeUser) return;
      setIsLoading(true);
      try {
        sendJsonMessage({
          userId: storeUser.id,
          type,
          action: "create",
        });
      } catch (error) {
        console.error("Failed to create policy:", error);
        setIsLoading(false);
      }
    },

    updateFromAdjustment: async (adjustment: PolicyAdjustment) => {
      if (!selectedPolicy || !storeUser) return;
      setIsLoading(true);

      removeContentFromPolicy(selectedPolicy);
      sendJsonMessage({
        userId: storeUser.id,
        policyId: selectedPolicy.id,
        adjustment,
        action: "update-from-adjustment",
      });
    },

    updateFromPrompt: async (prompt: string) => {
      if (!selectedPolicy || !storeUser) return;
      setIsLoading(true);

      removeContentFromPolicy(selectedPolicy);
      sendJsonMessage({
        userId: storeUser.id,
        policyId: selectedPolicy.id,
        prompt,
        action: "update-from-prompt",
      });
    },

    updateContent: async (markdownContent: string) => {
      if (!selectedPolicy || !storeUser) return;
      setIsLoading(true);

      try {
        const updatedPolicy = await apiUpdatePolicyProjectContent(
          selectedPolicy.id,
          markdownContent
        );

        //as a hack in case the adjustments havent been applied yet
        const contentUpdatedPolicy = {
          ...selectedPolicy,
          content: updatedPolicy.content,
        };

        if (contentUpdatedPolicy) {
          updatePolicyState(contentUpdatedPolicy);
        }
      } catch (error) {
        console.error("Failed to update policy content:", error);
      } finally {
        setIsLoading(false);
      }
    },

    updateTitle: async (newTitle: string) => {
      if (!selectedPolicy) return;
      setIsLoading(true);

      const updatedPolicy = await apiUpdatePolicyProject({
        id: selectedPolicy.id,
        title: newTitle,
      });
      if (updatedPolicy) {
        console.log("updateTitle->updatedPolicy", updatedPolicy);
        updatePolicyState(updatedPolicy);
      }
      setIsLoading(false);
    },

    uploadFile: async (file: File, type: PolicyProjectType) => {
      if (!storeUser) return;
      setIsLoading(true);

      const response = await postPolicyStudioFileUpload(
        storeUser.id,
        file,
        type
      );
      if (response.data.error) {
        toast("Unable to upload file, please try again.", toastFail);
        setIsLoading(false);
        console.error("Failed to upload file:", response.data.error);
      } else {
        const policyProject = response.data.data as PolicyProject;
        setPolicyProjects([policyProject, ...policyProjects]);
        setSelectedPolicy(policyProject);
        sendJsonMessage({
          userId: storeUser.id,
          policyId: policyProject.id,
          type,
          action: "create-from-file",
        });
      }
    },

    createFromLinkRecord: async (
      linkRecordId: string,
      type: PolicyProjectType
    ) => {
      if (!storeUser) return;
      setIsLoading(true);

      sendJsonMessage({
        userId: storeUser.id,
        type,
        action: "create-from-resource",
        linkRecordId,
      });
    },

    addAsResource: async (replaceExisting: boolean) => {
      if (!storeUser || !selectedPolicy) return;
      setIsLoading(true);
      const response = await apiEmbedPolicyProjectAsResource(
        selectedPolicy.id,
        replaceExisting,
        selectedPolicy?.originalLinkRecordId
      );
      if (response) {
        toast("Policy added to resources", toastSuccess);
        updatePolicyState(response);
      } else {
        toast("Unable to add policy to resources", toastFail);
      }
      setIsLoading(false);
    },

    findConflicts: async (linkRecordIds: string[]) => {
      if (!selectedPolicy?.id) return;
      setDrawerIsLoading(true);
      // const updatedPolicyProject = await apiCheckConflicts(
      //   selectedPolicy.id,
      //   linkRecordIds
      // );

      // if (updatedPolicyProject) {
      //   updatePolicyState(updatedPolicyProject);
      // }
      // setDrawerIsLoading(false);

      sendJsonMessage({
        policyId: selectedPolicy.id,
        conflictLinkRecordIds: linkRecordIds,
        action: "check-conflicts",
      });
    },

    checkCompliance: async (jurisdictionContent: string) => {
      if (!selectedPolicy?.id) return;
      setDrawerIsLoading(true);
      // const updatedPolicyProject = await apiCheckCompliance(
      //   selectedPolicy.id,
      //   jurisdictionContent
      // );
      // if (updatedPolicyProject) {
      //   updatePolicyState(updatedPolicyProject);
      // }
      sendJsonMessage({
        policyId: selectedPolicy.id,
        jurisdictionContent,
        action: "check-compliance",
      });

      // setDrawerIsLoading(false);
    },

    handleDelete: async (policyProjectId: string) => {
      setIsLoading(true);
      const response = await apiDeletePolicyProject(policyProjectId);
      if (response) {
        setPolicyProjects(
          policyProjects.filter((p) => p.id !== policyProjectId)
        );
        if (selectedPolicy?.id === policyProjectId) {
          setSelectedPolicy(null);
        }
      }
      setIsLoading(false);
    },

    exportToPdf: async (title: string, content: string) => {
      if (!selectedPolicy?.id) {
        toast("No policy selected for export", toastFail);
        return;
      }

      try {
        const html = showdownConverter.makeHtml(content);
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;
        tempDiv.style.cssText = `
          font-family: Arial, sans-serif;
          padding: 20px;
          max-width: 8.2in;
        `;

        const fileName = `${title}-${new Date().toISOString().split("T")[0]}.pdf`;
        const options = {
          margin: 0.5,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: { unit: "in", format: "letter" },
        };

        document.body.appendChild(tempDiv);
        await html2pdf().set(options).from(tempDiv).save();
        document.body.removeChild(tempDiv);

        toast("PDF downloaded successfully", toastSuccess);
      } catch (error) {
        console.error("PDF export failed:", error);
        toast("Failed to generate PDF", toastFail);
      }
    },

    handleAddNewClick: () => {
      setIsCreateModalOpen(true);
    },

    handleCloseCreateModal: () => {
      setIsCreateModalOpen(false);
    },

    handleCreateFromFileClick: () => {
      setIsCreateFromFileModalOpen(true);
    },

    handleCloseCreateFromFileModal: () => {
      setIsCreateFromFileModalOpen(false);
    },
  };

  const drawerActions = {
    openConflictsDrawer: () => setIsConflictsDrawerOpen(true),
    closeConflictsDrawer: () => {
      setIsConflictsDrawerOpen(false);
    },
    openComplianceDrawer: () => setIsComplianceDrawerOpen(true),
    closeComplianceDrawer: () => {
      setIsComplianceDrawerOpen(false);
    },
  };

  return {
    refs: {
      previewRef,
    },
    state: {
      policyProjects,
      selectedPolicy,
      isLoading,
      drawerIsLoading,
      isConflictsDrawerOpen,
      isComplianceDrawerOpen,
      linkRecords,
      isCreateModalOpen,
      isCreateFromFileModalOpen,
      isStreaming,
    },
    actions: {
      setSelectedPolicy,
      ...policyActions,
      ...drawerActions,
    },
  };
};
